import { useState } from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import type { LogEntry, Task } from "../../lib/types";
import { Button } from "../../components/ui/button";
import { StatusBadge } from "../../components/status-badge";
import { insightFromMessage, insightFromTask } from "../../lib/task-insights";
import { formatDate, copyText, shortText } from "../../lib/utils";

function logLine(log: LogEntry) {
  return `[${formatDate(log.created_at)}] [${log.level || "INFO"}] [${log.event || "-"}] ${log.message}`;
}

function levelClass(level: unknown) {
  const value = String(level || "INFO").toUpperCase();
  if (value === "ERROR") return "border-rose-200 bg-rose-50";
  if (value === "WARNING") return "border-amber-200 bg-amber-50";
  return "border-slate-200 bg-white";
}

export function TaskLogSheet({ task, logs }: { task: Task | null; logs: LogEntry[] }) {
  const [level, setLevel] = useState("");
  const filtered = logs.filter((log) => !level || String(log.level || "").toUpperCase() === level);
  const taskInsight = task ? insightFromTask(task) : null;
  return (
    <div className="space-y-3">
      {task ? (
        <section className="grid gap-2 rounded-md border bg-white p-3 text-xs">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="font-semibold">{task.task_type}</div>
              <div className="text-slate-500">{shortText(task.id, 12)}</div>
            </div>
            <StatusBadge status={task.status} />
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            <span>账号：{task.account_id || task.discord_account_id || "-"}</span>
            <span>更新：{formatDate(task.updated_at)}</span>
          </div>
          {task.error_message ? <div className="break-words rounded border border-rose-200 bg-rose-50 p-2 text-rose-800">{task.error_message}</div> : null}
          {task.execute_message ? <div className="break-words text-slate-600">{task.execute_message}</div> : null}
          {taskInsight ? (
            <div className="grid gap-1 rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-900">
              <div className="font-semibold">
                可读排障：{taskInsight.stage}
                {taskInsight.mailbox ? ` / 邮箱：${taskInsight.mailbox}` : ""}
                {taskInsight.node ? ` / 节点：${taskInsight.node}` : ""}
                {taskInsight.code ? ` / ${taskInsight.code}` : ""}
              </div>
              {taskInsight.hint ? <div className="break-words leading-5 [overflow-wrap:anywhere]">{taskInsight.hint}</div> : null}
            </div>
          ) : null}
        </section>
      ) : null}
      <div className="flex items-center justify-between gap-2">
        <select className="h-8 rounded-md border border-slate-300 px-2 text-xs" value={level} onChange={(event) => setLevel(event.target.value)}>
          <option value="">全部级别</option>
          <option value="INFO">INFO</option>
          <option value="WARNING">WARNING</option>
          <option value="ERROR">ERROR</option>
        </select>
        <Button
          onClick={() => {
            copyText(filtered.map(logLine).join("\n"));
            toast.success("日志已复制");
          }}
        >
          <Copy size={14} />
          复制全部
        </Button>
      </div>
      <div className="max-h-[70vh] overflow-auto rounded-md border">
        {filtered.length ? filtered.map((log) => {
            const insight = insightFromMessage(log.message);
            return (
              <div key={log.id} className={`border-b p-2 text-xs last:border-b-0 ${levelClass(log.level)}`}>
                <div className="mb-1 flex items-center justify-between text-slate-500">
                  <span>{log.level || "INFO"} / {log.event || "-"}</span>
                  <span className="flex items-center gap-2">
                    {formatDate(log.created_at)}
                    <button
                      className="text-slate-500 hover:text-slate-900"
                      onClick={() => {
                        copyText(logLine(log));
                        toast.success("单条日志已复制");
                      }}
                    >
                      <Copy size={12} />
                    </button>
                  </span>
                </div>
                <div className="whitespace-pre-wrap break-words leading-5 [overflow-wrap:anywhere]">{log.message}</div>
                {insight?.hint ? (
                  <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-amber-800">
                    {insight.stage}
                    {insight.mailbox ? ` / 邮箱：${insight.mailbox}` : ""}
                    {insight.node ? ` / 节点：${insight.node}` : ""}
                    ：{insight.hint}
                  </div>
                ) : null}
              </div>
            );
          }) : <div className="p-3 text-xs text-slate-500">暂无日志</div>}
      </div>
      {task ? (
        <details className="rounded-md border bg-white p-3 text-xs">
          <summary className="cursor-pointer font-semibold text-slate-500">开发详情</summary>
          <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded bg-slate-950 p-3 text-slate-50 [overflow-wrap:anywhere]">
            {JSON.stringify({ params: task.params, result: task.result, callback_url: task.callback_url, source: task.source }, null, 2)}
          </pre>
        </details>
      ) : null}
    </div>
  );
}
